import { z } from "zod";

// Base schemas for common properties
export const timestampSchema = z.object({
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});

// User schema
export const userSchema = z.object({
  id: z.number(),
  email: z.string().email(),
  first_name: z.string(),
  last_name: z.string(),
  coach_viewer: z.boolean(),
}).merge(timestampSchema);

export type UserSchema = z.infer<typeof userSchema>;

export const loginUserSchema = z.object({
  email: z.string().email(),
  name: z.string(),
});

export type LoginUserSchema = z.infer<typeof loginUserSchema>;

// Login request schema
export const loginRequestSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

export type LoginRequest = z.infer<typeof loginRequestSchema>;

// Login response schema
export const loginResponseSchema = z.object({
  user: loginUserSchema,
});

// Signup request schema
export const signupRequestSchema = z.object({
  first_name: z.string().min(1, "First name is required"),
  last_name: z.string().min(1, "Last name is required"),
  email: z.string().email("Invalid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
  password_confirmation: z.string(),
}).refine((data) => data.password === data.password_confirmation, {
  message: "Passwords don't match",
  path: ["password_confirmation"],
});

export type SignupRequest = z.infer<typeof signupRequestSchema>;

// Signup response schema
export const signupResponseSchema = z.object({
  user: loginUserSchema,
});

// Forgot password request schema
export const forgotPasswordRequestSchema = z.object({
  email: z.string().email("Invalid email address"),
});

export type ForgotPasswordRequest = z.infer<typeof forgotPasswordRequestSchema>;

// Forgot password response schema
export const forgotPasswordResponseSchema = z.object({
  message: z.string(),
});

// Reset password request schema
export const resetPasswordRequestSchema = z.object({
  reset_password_token: z.string().min(1, "Reset token is required"),
  password: z.string().min(6, "Password must be at least 6 characters"),
  password_confirmation: z.string(),
}).refine((data) => data.password === data.password_confirmation, {
  message: "Passwords don't match",
  path: ["password_confirmation"],
});

export type ResetPasswordRequest = z.infer<typeof resetPasswordRequestSchema>;

// Reset password response schema
export const resetPasswordResponseSchema = z.object({
  user: loginUserSchema,
});

// User License schema
export const userLicenseSchema = z.object({
  id: z.number(),
  license_type: z.enum(["free", "pro"]),
  status: z.enum(["active", "expired", "cancelled", "past_due", "free_trial", "pending"]),
  start_date: z.string().datetime(),
  end_date: z.string().datetime(),
  current_month_start: z.string().datetime(),
  days_until_reset: z.number(),

  // Separate game tracking
  pro_games: z.object({
    used_this_month: z.number(),
    limit: z.number(),
    remaining: z.number(),
    can_create: z.boolean(),
    unlimited: z.boolean(),
  }),

  free_games: z.object({
    used_this_month: z.number(),
    limit: z.number(),
    remaining: z.number(),
    can_create: z.boolean(),
    unlimited: z.boolean(),
  }),

  // Overall license status
  is_pro: z.boolean(),
  is_active: z.boolean(),

  stripe: z.object({
    subscription_id: z.string(),
    customer_id: z.string().nullable(),
    plan_type: z.string().nullable(),
  }).nullable(),
});

export type UserLicense = z.infer<typeof userLicenseSchema>;

// User License response schema
export const userLicenseResponseSchema = z.object({
  license: userLicenseSchema,
});

// Stripe checkout response schema
export const stripeCheckoutResponseSchema = z.object({
  checkout_url: z.string().url(),
  session_id: z.string(),
});

// Stripe billing portal response schema
export const stripeBillingPortalResponseSchema = z.object({
  portal_url: z.string().url(),
});

// API Error schema
export const apiErrorSchema = z.object({
  message: z.string(),
  errors: z.record(z.array(z.string())).optional(),
});

// Game limit error schema
export const gameLimitErrorSchema = z.object({
  error: z.string(),
  license: z.object({
    type: z.string(),
    games_remaining: z.number(),
    days_until_reset: z.number(),
  }),
});
